import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import {
  mockFreelancerProfiles,
  mockFreelancerReviews,
  type FreelancerProfile,
  type FreelancerReview,
} from '@/mocks/freelancers';
import { mockGigs, type Gig } from '@/mocks/gigs';
import { mapDbGig, formatGigPrice } from '@/lib/gigs';
import {
  mapDbFreelancerProfile,
  mapDbFreelancerReview,
  initialsFor,
  splitSkills,
  averageRating,
  formatReviewDate,
} from '@/lib/freelancers';

function FreelancerProfilePage() {
  const { ownerId } = useParams<{ ownerId: string }>();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { copied, copy } = useCopyToClipboard();

  const [freelancer, setFreelancer] = useState<FreelancerProfile | null>(null);
  const [gigs, setGigs] = useState<Gig[]>([]);
  const [reviews, setReviews] = useState<FreelancerReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const load = async () => {
    if (!ownerId) return;
    setLoading(true);
    setError('');
    try {
      const { data: profileData, error: profileErr } = await supabase
        .from('freelancer_profiles')
        .select('*')
        .eq('owner_id', ownerId)
        .maybeSingle();
      if (profileErr) throw profileErr;

      if (profileData) {
        const prof = mapDbFreelancerProfile(profileData as never);
        setFreelancer(prof);

        const [gigsRes, reviewsRes] = await Promise.all([
          supabase
            .from('gigs')
            .select('*')
            .eq('owner_id', ownerId)
            .eq('status', 'active')
            .order('created_at', { ascending: false }),
          supabase
            .from('freelancer_reviews')
            .select('*')
            .eq('freelancer_id', prof.id)
            .eq('status', 'active')
            .order('created_at', { ascending: false }),
        ]);

        if (gigsRes.data && gigsRes.data.length > 0) {
          setGigs((gigsRes.data as unknown[]).map((row) => mapDbGig(row as never)));
        } else {
          setGigs(mockGigs.filter((g) => g.ownerId === ownerId));
        }

        if (reviewsRes.data && reviewsRes.data.length > 0) {
          const rows = reviewsRes.data as { user_id: string }[];
          const userIds = [...new Set(rows.map((r) => r.user_id))];
          const { data: reviewerData } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', userIds);
          const nameMap: Record<string, string> = {};
          (reviewerData || []).forEach((p) => {
            nameMap[(p as { id: string }).id] = (p as { full_name: string | null }).full_name || '';
          });
          setReviews(
            rows.map((r) => {
              const name = nameMap[r.user_id] || 'Client';
              return mapDbFreelancerReview(r as never, name, initialsFor(name));
            }),
          );
        } else {
          setReviews(mockFreelancerReviews[ownerId] || []);
        }
      } else {
        setFreelancer(mockFreelancerProfiles.find((p) => p.ownerId === ownerId) || null);
        setGigs(mockGigs.filter((g) => g.ownerId === ownerId));
        setReviews(mockFreelancerReviews[ownerId] || []);
      }
    } catch {
      setFreelancer(mockFreelancerProfiles.find((p) => p.ownerId === ownerId) || null);
      setGigs(mockGigs.filter((g) => g.ownerId === ownerId));
      setReviews(mockFreelancerReviews[ownerId] || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId]);

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      navigate('/auth/login', {
        state: { from: { pathname: `/freelancers/${ownerId}` } },
      });
      return;
    }
    if (!freelancer) return;
    if (freelancer.ownerId === user.id) {
      setFormError('Vous ne pouvez pas laisser un avis sur votre propre profil.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    setFormSuccess('');

    const reviewerName = profile?.full_name || user.email?.split('@')[0] || 'Vous';
    const newReview: FreelancerReview = {
      id: `local-${Date.now()}`,
      reviewerName,
      reviewerInitials: initialsFor(reviewerName),
      rating,
      comment: comment.trim(),
      date: new Date().toISOString().slice(0, 10),
    };

    const { error: err } = await supabase.from('freelancer_reviews').insert({
      freelancer_id: freelancer.id,
      user_id: user.id,
      rating,
      comment: comment.trim(),
    });

    setSubmitting(false);

    if (err) {
      // Fallback démo : ajout optimiste si la cible n'est pas un profil réel en base
      setReviews((prev) => [newReview, ...prev]);
      setComment('');
      setRating(5);
      setFormSuccess('Merci, votre avis a bien été ajouté !');
      return;
    }

    setReviews((prev) => [newReview, ...prev]);
    setComment('');
    setRating(5);
    setFormSuccess('Merci, votre avis a bien été publié !');
  };

  const skills = freelancer ? splitSkills(freelancer.skills) : [];
  const avgRating = averageRating(reviews);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/10">
            <i className="ri-loader-4-line text-2xl text-accent-400 animate-spin" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!freelancer) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-500/10">
              <i className="ri-user-search-line text-3xl text-accent-400" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">
              Profil freelance introuvable
            </h1>
            <p className="text-sm text-foreground-400 mb-6">
              Ce profil n&apos;existe pas encore ou a été retiré.
            </p>
            <Link
              to="/work"
              className="inline-block px-6 py-2.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm"
            >
              Découvrir les services
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-24 md:pt-32 pb-16">
          {/* Header */}
          <nav
            aria-label="Breadcrumb"
            className="text-xs text-foreground-400 mb-6 flex items-center gap-2 flex-wrap"
          >
            <Link to="/work" className="hover:text-accent-400 transition-colors">Work</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-500">Profil freelance</span>
          </nav>

          <section className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70 mb-6">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-background-200/60 shrink-0 flex items-center justify-center">
                {freelancer.avatarUrl ? (
                  <img
                    src={freelancer.avatarUrl}
                    alt={freelancer.displayName}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-heading font-bold text-3xl text-accent-400">
                    {initialsFor(freelancer.displayName)}
                  </span>
                )}
              </div>

              <div className="flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                  <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 leading-tight">
                      {freelancer.displayName}
                    </h1>
                    {freelancer.headline && (
                      <p className="text-foreground-400 mt-1">{freelancer.headline}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <i className="ri-map-pin-line text-accent-400" />
                      {freelancer.location}
                    </span>
                    {freelancer.memberSince && (
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line text-accent-400" />
                        Membre depuis {freelancer.memberSince}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => copy(window.location.href)}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-background-300/60 text-foreground-50 rounded-lg text-xs font-medium hover:bg-background-200/60 transition-colors whitespace-nowrap"
                    >
                      <i className={copied ? 'ri-check-line text-accent-400' : 'ri-link'} />
                      {copied ? 'Lien copié !' : 'Copier le lien'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-4">
                  {avgRating !== null ? (
                    <span className="inline-flex items-center gap-1.5">
                      <i className="ri-star-fill text-accent-400 text-lg" />
                      <span className="font-heading font-bold text-xl text-foreground-50">
                        {avgRating.toFixed(1)}
                      </span>
                      <span className="text-sm text-foreground-400">
                        ({reviews.length} avis)
                      </span>
                    </span>
                  ) : (
                    <span className="text-sm text-foreground-400">Aucun avis pour le moment</span>
                  )}
                </div>

                {skills.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-4">
                    {skills.map((skill) => (
                      <span
                        key={skill}
                        className="text-xs font-medium px-2.5 py-1 rounded-full bg-accent-500/10 text-accent-400 whitespace-nowrap"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}

                {(freelancer.phone || freelancer.website) && (
                  <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-background-200/70">
                    {freelancer.phone && (
                      <a
                        href={`tel:${freelancer.phone.replace(/\s/g, '')}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm whitespace-nowrap"
                      >
                        <i className="ri-phone-line" />
                        {freelancer.phone}
                      </a>
                    )}
                    {freelancer.phone && (
                      <a
                        href={`https://wa.me/${freelancer.phone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line" />
                        WhatsApp
                      </a>
                    )}
                    {freelancer.website && (
                      <a
                        href={
                          freelancer.website.startsWith('http')
                            ? freelancer.website
                            : `https://${freelancer.website}`
                        }
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-background-300/60 text-foreground-50 rounded-lg hover:bg-background-200/60 transition-colors text-sm font-semibold whitespace-nowrap"
                      >
                        <i className="ri-global-line" />
                        Site web
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {freelancer.bio && (
              <p className="text-sm text-foreground-400 leading-relaxed mt-6 pt-6 border-t border-background-200/70">
                {freelancer.bio}
              </p>
            )}
          </section>

          {/* Portfolio */}
          <section className="mb-8">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-5 flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
                <i className="ri-briefcase-4-line text-accent-400" />
              </span>
              Portfolio · {gigs.length} service{gigs.length > 1 ? 's' : ''}
            </h2>

            {gigs.length === 0 ? (
              <p className="text-sm text-foreground-400 py-8 text-center bg-background-100 border border-background-200/70 rounded-xl">
                Ce freelance n&apos;a pas encore publié de service.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {gigs.map((gig) => (
                  <article
                    key={gig.id}
                    className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-accent-400/40 transition-all duration-300 flex flex-col"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={gig.cover}
                        alt={`Service ${gig.title}`}
                        title={`${gig.title} - ${gig.category}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute top-3 left-3 text-xs font-medium px-2.5 py-1 rounded-full bg-background-50/90 text-foreground-50 whitespace-nowrap">
                        {gig.category}
                      </span>
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <Link to={`/work/${gig.id}`}>
                        <h3 className="font-heading font-semibold text-foreground-50 leading-snug hover:text-accent-400 transition-colors line-clamp-2">
                          {gig.title}
                        </h3>
                      </Link>
                      <div className="mt-auto pt-4 flex items-center justify-between gap-3">
                        <span className="font-semibold text-accent-400 whitespace-nowrap">
                          {formatGigPrice(gig.price)}
                        </span>
                        {gig.rating !== null && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-400 whitespace-nowrap">
                            <i className="ri-star-fill" />
                            {gig.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          {/* Reviews */}
          <section>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-5 flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-500/10">
                <i className="ri-chat-3-line text-accent-400" />
              </span>
              Avis clients ({reviews.length})
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Review list */}
              <div className="lg:col-span-2 space-y-4">
                {reviews.length === 0 ? (
                  <p className="text-sm text-foreground-400 py-8 text-center bg-background-100 border border-background-200/70 rounded-xl">
                    Soyez le premier à laisser un avis sur ce freelance.
                  </p>
                ) : (
                  reviews.map((rev) => (
                    <div
                      key={rev.id}
                      className="p-5 rounded-xl bg-background-100 border border-background-200/70"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-accent-500/10 flex items-center justify-center shrink-0">
                          <span className="text-sm font-semibold text-accent-400">
                            {rev.reviewerInitials}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <span className="font-semibold text-foreground-50 text-sm">
                              {rev.reviewerName}
                            </span>
                            <span className="text-xs text-foreground-400 whitespace-nowrap">
                              {formatReviewDate(rev.date)}
                            </span>
                          </div>
                          <div className="flex items-center gap-0.5 my-1.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <i
                                key={s}
                                className={`ri-star-fill text-sm ${
                                  s <= rev.rating ? 'text-accent-400' : 'text-foreground-500'
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-sm text-foreground-400 leading-relaxed">
                            {rev.comment}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Review form */}
              <div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 lg:sticky lg:top-24">
                  <h3 className="font-heading font-semibold text-foreground-50 mb-4">
                    Laisser un avis
                  </h3>

                  {!user ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-foreground-400 mb-4">
                        Connectez-vous pour partager votre expérience avec ce freelance.
                      </p>
                      <button
                        onClick={() =>
                          navigate('/auth/login', {
                            state: { from: { pathname: `/freelancers/${ownerId}` } },
                          })
                        }
                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm whitespace-nowrap"
                      >
                        <i className="ri-login-box-line" />
                        Se connecter
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmitReview}>
                      {formError && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400 mb-4">
                          {formError}
                        </div>
                      )}
                      {formSuccess && (
                        <div className="p-3 rounded-lg bg-accent-500/10 border border-accent-400/30 text-sm text-accent-400 mb-4">
                          {formSuccess}
                        </div>
                      )}

                      <label className="block text-sm font-medium text-foreground-400 mb-2">
                        Votre note
                      </label>
                      <div className="flex items-center gap-1 mb-4">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setRating(s)}
                            onMouseEnter={() => setHoverRating(s)}
                            onMouseLeave={() => setHoverRating(0)}
                            className="w-8 h-8 flex items-center justify-center cursor-pointer"
                            aria-label={`Note ${s} sur 5`}
                          >
                            <i
                              className={`ri-star-fill text-2xl transition-colors ${
                                s <= (hoverRating || rating)
                                  ? 'text-accent-400'
                                  : 'text-foreground-500'
                              }`}
                            />
                          </button>
                        ))}
                      </div>

                      <label
                        htmlFor="review-comment"
                        className="block text-sm font-medium text-foreground-400 mb-2"
                      >
                        Votre commentaire
                      </label>
                      <textarea
                        id="review-comment"
                        name="comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={4}
                        maxLength={500}
                        placeholder="Racontez votre expérience..."
                        className="w-full bg-background-50 border border-background-300/60 rounded-md px-3 py-2.5 text-sm text-foreground-50 placeholder:text-foreground-500 outline-none focus:border-accent-400 resize-none"
                      />
                      <p className="text-xs text-foreground-500 mt-1 mb-4">
                        {comment.length}/500 caractères
                      </p>

                      <button
                        type="submit"
                        disabled={submitting || comment.trim().length === 0}
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-accent-500 text-foreground-950 font-semibold rounded-lg hover:bg-accent-400 transition-colors text-sm whitespace-nowrap disabled:opacity-60"
                      >
                        <i className="ri-send-plane-line" />
                        {submitting ? 'Envoi...' : 'Publier mon avis'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default FreelancerProfilePage;